import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import SuspendedUserModal from './SuspendedUserModal';

interface LoginFormProps {
  onSuccess?: () => void;
  onFailure?: (message: string) => void;
  onSwitchToRegister?: () => void;
  onForgotPassword?: () => void;
  onClose?: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ onSuccess, onFailure, onSwitchToRegister, onForgotPassword, onClose }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showSuspensionModal, setShowSuspensionModal] = useState(false);
  const [suspensionReason, setSuspensionReason] = useState('');
  const { login } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await login(username, password);
      if (response.success && response.suspended) {
        // Handle suspended user - they're logged in but show suspension modal
        setSuspensionReason(response.suspensionReason || 'Account suspended');
        setShowSuspensionModal(true);
        setPassword(''); // Clear password
        // Don't call onSuccess() - keep them in suspension modal
      } else if (response.success) {
        onSuccess?.();
      } else {
        onFailure?.(response.message || 'Login failed');
        // Clear password but preserve username on failure
        setPassword('');
      }
    } catch (err) {
      onFailure?.(err instanceof Error ? err.message : 'Login failed');
      // Clear password but preserve username on failure
      setPassword('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="auth-form">
        {onClose && (
          <button className="popup-close-btn" onClick={onClose}>✕</button>
        )}
        <h2>Login</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="username">Username or Email:</label>
            <input
              type="text"
              id="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              disabled={loading}
            />
          </div>
          <div className="form-group">
            <label htmlFor="password">Password:</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                style={{ paddingRight: '40px' }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  color: '#666',
                  padding: '4px'
                }}
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
          </div>
          <button type="submit" disabled={loading}>
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>
        
        <div style={{textAlign: 'center', marginTop: '1rem'}}>
          <button 
            type="button" 
            className="link-button" 
            onClick={onForgotPassword}
            disabled={loading}
            style={{fontSize: '0.9rem', color: '#666'}}
          >
            Forgot Password?
          </button>
        </div>
        
        <p style={{marginTop: '1.5rem'}}>
          Don't have an account?{' '}
          <button 
            type="button" 
            className="link-button" 
            onClick={onSwitchToRegister}
            disabled={loading}
          >
            Register here
          </button>
        </p>
      </div>

      {/* Suspended User Modal */}
      <SuspendedUserModal
        isOpen={showSuspensionModal}
        onClose={() => setShowSuspensionModal(false)}
        suspensionReason={suspensionReason}
      />
    </>
  );
};

export default LoginForm;